import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaCrown,
  FaSearch,
  FaSync,
  FaUserPlus,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaRedo,
  FaTrash,
  FaChevronDown,
} from 'react-icons/fa';
import ConfirmDialog from './ConfirmDialog';
import DiscordUserChip from './DiscordUserChip';
import './VipPanel.css';

const STATUS_META = {
  active: { label: 'Ativo', tone: 'ok' },
  past_due: { label: 'Em atraso', tone: 'warn' },
  canceled: { label: 'Cancelado', tone: 'danger' },
  incomplete: { label: 'Incompleto', tone: 'muted' },
  unpaid: { label: 'Não pago', tone: 'danger' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const formatMoney = (cents) => {
  if (cents == null) return '—';
  return (Number(cents) / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const shortPlan = (name) =>
  String(name || '').replace(/^Nexus VIP\s+/i, '') || '—';

const emptyGrant = () => ({
  discordId: '',
  minecraftNick: '',
  planId: 'origin',
  billing: 'monthly',
  paymentMethod: 'card',
});

/**
 * Gestão profissional de assinaturas VIP.
 */
const VipPanel = ({
  stats,
  subscriptions = [],
  total = 0,
  catalog,
  statusFilter,
  planFilter,
  query,
  loading = false,
  actionBusy = null,
  message = '',
  error = '',
  canEdit = true,
  canDelete = true,
  onStatusFilter,
  onPlanFilter,
  onQueryChange,
  onSearch,
  onRefresh,
  onSelect,
  selected,
  onCloseDetail,
  onRevoke,
  onReapply,
  onReactivate,
  onDelete,
  onUpdate,
  onGrant,
  grantOpen,
  onToggleGrant,
  guildMembers = [],
  guildMembersLoading = false,
  guildMembersError = '',
  onReloadMembers,
}) => {
  const [grantForm, setGrantForm] = useState(emptyGrant);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberMenuOpen, setMemberMenuOpen] = useState(false);
  const [editNick, setEditNick] = useState('');
  const [editPlan, setEditPlan] = useState('');
  const [editBilling, setEditBilling] = useState('monthly');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const memberPickerRef = useRef(null);
  const memberInputRef = useRef(null);

  const plans = catalog?.plans || [];

  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    return guildMembers
      .filter((m) => {
        if (!q) return true;
        const hay = [m.displayName, m.globalName, m.username, m.nick, m.id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 80);
  }, [guildMembers, memberQuery]);

  const rawIdDraft = String(memberQuery || '').trim();
  const canUseRawId = /^\d{5,32}$/.test(rawIdDraft);

  useEffect(() => {
    if (!selected) return;
    setEditNick(selected.minecraftNick || '');
    setEditPlan(selected.planId || 'origin');
    setEditBilling(selected.billing || 'monthly');
  }, [selected]);

  useEffect(() => {
    if (grantOpen) return;
    setGrantForm(emptyGrant());
    setSelectedMember(null);
    setMemberQuery('');
    setMemberMenuOpen(false);
  }, [grantOpen]);

  useEffect(() => {
    if (!memberMenuOpen) return undefined;
    const onDoc = (e) => {
      if (memberPickerRef.current && !memberPickerRef.current.contains(e.target)) {
        setMemberMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMemberMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [memberMenuOpen]);

  const closeConfirm = () => {
    if (actionBusy) return;
    setConfirmDialog(null);
  };

  const runConfirm = () => {
    const fn = confirmDialog?.onConfirm;
    setConfirmDialog(null);
    fn?.();
  };

  const pickMember = (memberOrId) => {
    const isMember = memberOrId && typeof memberOrId === 'object';
    const id = String(isMember ? memberOrId.id : memberOrId || '').trim();
    if (!/^\d{5,32}$/.test(id)) return;
    setGrantForm((f) => ({ ...f, discordId: id }));
    setSelectedMember(
      isMember
        ? memberOrId
        : guildMembers.find((m) => String(m.id) === id) || { id }
    );
    setMemberQuery('');
    setMemberMenuOpen(false);
  };

  const clearMember = () => {
    setGrantForm((f) => ({ ...f, discordId: '' }));
    setSelectedMember(null);
    setMemberQuery('');
    setTimeout(() => memberInputRef.current?.focus(), 0);
  };

  const submitGrant = (e) => {
    e.preventDefault();
    if (!/^\d{5,32}$/.test(String(grantForm.discordId || '').trim())) return;
    onGrant?.({ ...grantForm });
  };

  return (
    <section className="admin-section vip-panel">
      <header className="admin-section__head vip-panel__head">
        <div>
          <h1>
            <FaCrown aria-hidden="true" /> Gestão VIP
          </h1>
          <p>
            Assinaturas, sincronização Discord/LuckPerms e concessão manual
            {total != null ? ` · ${total} resultado${total === 1 ? '' : 's'}` : ''}
          </p>
        </div>
        <div className="vip-panel__head-actions">
          <button
            type="button"
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={onRefresh}
            disabled={loading}
          >
            <FaSync className={loading ? 'admin-spin' : ''} aria-hidden="true" />
            Atualizar
          </button>
          {canEdit && (
            <button
              type="button"
              className="admin-btn admin-btn--primary admin-btn--sm"
              onClick={onToggleGrant}
            >
              <FaUserPlus aria-hidden="true" />
              {grantOpen ? 'Fechar concessão' : 'Conceder VIP'}
            </button>
          )}
        </div>
      </header>

      {message && <p className="admin-success">{message}</p>}
      {error && (
        <div className="admin-alert" role="alert">
          {error}
        </div>
      )}

      <div className="admin-stats admin-stats--wide vip-stats">
        <div className="admin-stat">
          <span className="admin-stat__label">Ativos</span>
          <strong className="admin-stat__value">{stats?.active ?? '—'}</strong>
          <span className="admin-stat__sub">
            {stats?.newLast7 != null ? `+${stats.newLast7} em 7 dias` : '—'}
          </span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Em atraso</span>
          <strong className="admin-stat__value">{stats?.pastDue ?? '—'}</strong>
          <span className="admin-stat__sub">past_due</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Vencem em 7 dias</span>
          <strong className="admin-stat__value">{stats?.expiringSoon ?? '—'}</strong>
          <span className="admin-stat__sub">renovação / PIX</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Cancelados</span>
          <strong className="admin-stat__value">{stats?.canceled ?? '—'}</strong>
          <span className="admin-stat__sub">
            Total histórico: {stats?.total ?? '—'}
          </span>
        </div>
      </div>

      {stats?.byPlan && Object.keys(stats.byPlan).length > 0 && (
        <div className="vip-plan-strip">
          {Object.values(stats.byPlan).map((p) => (
            <button
              key={p.id}
              type="button"
              className={`vip-plan-chip${planFilter === p.id ? ' is-active' : ''}`}
              onClick={() =>
                onPlanFilter(planFilter === p.id ? '' : p.id)
              }
            >
              <strong>{p.count}</strong>
              <span>{shortPlan(p.name)}</span>
            </button>
          ))}
        </div>
      )}

      {canEdit && grantOpen && (
        <form className="vip-grant" onSubmit={submitGrant}>
          <header>
            <h2>Conceder VIP manualmente</h2>
            <p>
              Escolha o membro do servidor, o nick Minecraft e o plano. A
              sincronização Discord + LuckPerms roda na hora.
            </p>
          </header>

          <div className="vip-grant__member">
            <span className="vip-grant__label">Membro Discord</span>
            {selectedMember?.id ? (
              <div className="vip-grant__selected">
                <DiscordUserChip
                  user={selectedMember}
                  userId={selectedMember.id}
                />
                <button
                  type="button"
                  className="admin-btn admin-btn--ghost admin-btn--sm"
                  onClick={clearMember}
                  aria-label="Trocar membro"
                >
                  <FaTimes aria-hidden="true" /> Trocar
                </button>
              </div>
            ) : (
              <div
                className={`vip-member-picker${memberMenuOpen ? ' is-open' : ''}`}
                ref={memberPickerRef}
              >
                <div
                  className={`vip-member-picker__control${guildMembersLoading ? ' is-loading' : ''}`}
                  onClick={() => {
                    if (guildMembersLoading) return;
                    setMemberMenuOpen(true);
                    setTimeout(() => memberInputRef.current?.focus(), 0);
                  }}
                >
                  <FaSearch
                    className="vip-member-picker__icon"
                    aria-hidden="true"
                  />
                  <input
                    ref={memberInputRef}
                    type="text"
                    value={memberQuery}
                    disabled={guildMembersLoading}
                    placeholder={
                      guildMembersLoading
                        ? 'Carregando membros…'
                        : 'Buscar membro pelo nome…'
                    }
                    onChange={(e) => {
                      setMemberQuery(e.target.value);
                      setMemberMenuOpen(true);
                    }}
                    onFocus={() => setMemberMenuOpen(true)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && canUseRawId) {
                        e.preventDefault();
                        pickMember(rawIdDraft);
                      }
                    }}
                    aria-required="true"
                  />
                  <FaChevronDown
                    className="vip-member-picker__chevron"
                    aria-hidden="true"
                  />
                </div>

                {memberMenuOpen && !guildMembersLoading && (
                  <div className="vip-member-picker__menu" role="listbox">
                    {guildMembersError && guildMembers.length === 0 ? (
                      <p className="vip-member-picker__empty">
                        {guildMembersError}
                        {canUseRawId
                          ? ' Você ainda pode colar o ID e pressionar Enter.'
                          : ''}
                        {onReloadMembers ? (
                          <>
                            {' '}
                            <button
                              type="button"
                              className="vip-member-picker__retry"
                              onClick={onReloadMembers}
                            >
                              Tentar de novo
                            </button>
                          </>
                        ) : null}
                      </p>
                    ) : filteredMembers.length === 0 ? (
                      <p className="vip-member-picker__empty">
                        {guildMembers.length === 0
                          ? 'Nenhum membro carregado.'
                          : memberQuery.trim()
                            ? canUseRawId
                              ? 'Nenhum resultado — pressione Enter para usar este ID.'
                              : 'Nenhum membro encontrado.'
                            : 'Digite para filtrar os membros.'}
                      </p>
                    ) : (
                      filteredMembers.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          className="vip-member-picker__option"
                          role="option"
                          onClick={() => pickMember(m)}
                        >
                          <DiscordUserChip user={m} userId={m.id} compact />
                          {m.username && m.displayName !== m.username ? (
                            <span className="vip-member-picker__username">
                              @{m.username}
                            </span>
                          ) : null}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            {canUseRawId && !selectedMember?.id && (
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm vip-grant__raw-id"
                onClick={() => pickMember(rawIdDraft)}
              >
                Usar ID {rawIdDraft}
              </button>
            )}
          </div>

          <div className="vip-grant__grid">
            <label>
              Nick Minecraft
              <input
                value={grantForm.minecraftNick}
                onChange={(e) =>
                  setGrantForm((f) => ({
                    ...f,
                    minecraftNick: e.target.value,
                  }))
                }
                placeholder="Steve"
                required
                maxLength={16}
              />
            </label>
            <label>
              Plano
              <select
                value={grantForm.planId}
                onChange={(e) =>
                  setGrantForm((f) => ({ ...f, planId: e.target.value }))
                }
              >
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {shortPlan(p.name)} · {formatMoney(p.monthlyCents)}/mês
                  </option>
                ))}
              </select>
            </label>
            <label>
              Ciclo
              <select
                value={grantForm.billing}
                onChange={(e) =>
                  setGrantForm((f) => ({ ...f, billing: e.target.value }))
                }
              >
                <option value="monthly">Mensal</option>
                <option value="annual">Anual</option>
              </select>
            </label>
            <label>
              Método
              <select
                value={grantForm.paymentMethod}
                onChange={(e) =>
                  setGrantForm((f) => ({
                    ...f,
                    paymentMethod: e.target.value,
                  }))
                }
              >
                <option value="card">Cartão (Stripe)</option>
                <option value="pix">PIX</option>
              </select>
            </label>
          </div>
          <div className="vip-grant__actions">
            <button
              type="submit"
              className="admin-btn admin-btn--primary"
              disabled={
                actionBusy === 'grant' ||
                !/^\d{5,32}$/.test(String(grantForm.discordId || '').trim())
              }
            >
              {actionBusy === 'grant' ? 'Concedendo…' : 'Confirmar concessão'}
            </button>
            <button
              type="button"
              className="admin-btn admin-btn--ghost"
              onClick={() => {
                setGrantForm(emptyGrant());
                setSelectedMember(null);
                setMemberQuery('');
                onToggleGrant();
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="admin-toolbar vip-toolbar">
        <div className="admin-filters">
          {['active', 'past_due', 'canceled', 'all'].map((s) => (
            <button
              key={s}
              type="button"
              className={`admin-chip${statusFilter === s ? ' admin-chip--active' : ''}`}
              onClick={() => onStatusFilter(s)}
            >
              {s === 'all' ? 'Todos' : STATUS_META[s]?.label || s}
            </button>
          ))}
        </div>
        <form
          className="admin-search"
          onSubmit={(e) => {
            e.preventDefault();
            onSearch();
          }}
        >
          <FaSearch aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Nick, Discord ID, Stripe ou MP…"
          />
        </form>
      </div>

      <div className={`vip-layout${selected ? ' has-detail' : ''}`}>
        <div className="admin-table-wrap vip-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Jogador</th>
                <th>Plano</th>
                <th>Status</th>
                <th>Pagamento</th>
                <th>Período</th>
                <th>Criado</th>
              </tr>
            </thead>
            <tbody>
              {loading && subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <p className="admin-empty">Carregando assinaturas…</p>
                  </td>
                </tr>
              ) : subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <p className="admin-empty">Nenhuma assinatura encontrada.</p>
                  </td>
                </tr>
              ) : (
                subscriptions.map((s) => {
                  const meta = STATUS_META[s.status] || {
                    label: s.status,
                    tone: 'muted',
                  };
                  const isSel = selected?.id === s.id;
                  return (
                    <tr
                      key={s.id}
                      className={`admin-table__row vip-row${isSel ? ' is-selected' : ''}`}
                      onClick={() => onSelect(s)}
                    >
                      <td>
                        <div className="vip-player">
                          <strong>{s.minecraftNick || '—'}</strong>
                          <DiscordUserChip
                            user={s.discordUser}
                            userId={s.discordId}
                            compact
                          />
                        </div>
                      </td>
                      <td>
                        <span className="vip-plan-name">
                          {shortPlan(s.planName)}
                        </span>
                        <small className="vip-muted">
                          {s.billing === 'annual' ? 'Anual' : 'Mensal'}
                        </small>
                      </td>
                      <td>
                        <span className={`vip-badge vip-badge--${meta.tone}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td>
                        {s.paymentMethod === 'pix'
                          ? 'PIX'
                          : s.paymentMethod === 'card'
                            ? 'Cartão'
                            : '—'}
                      </td>
                      <td>{formatDate(s.currentPeriodEnd)}</td>
                      <td>{formatDate(s.createdAt)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {selected && (
          <aside className="vip-detail">
            <header className="vip-detail__head">
              <div>
                <h2>{selected.minecraftNick || 'Assinatura'}</h2>
                <DiscordUserChip
                  user={selected.discordUser}
                  userId={selected.discordId}
                  compact
                />
              </div>
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm"
                onClick={onCloseDetail}
                aria-label="Fechar"
              >
                <FaTimes aria-hidden="true" />
              </button>
            </header>

            <div className="vip-detail__badge-row">
              <span
                className={`vip-badge vip-badge--${STATUS_META[selected.status]?.tone || 'muted'}`}
              >
                {STATUS_META[selected.status]?.label || selected.status}
              </span>
              <span className="vip-plan-pill">{shortPlan(selected.planName)}</span>
            </div>

            <dl className="vip-detail__meta">
              <div>
                <dt>Ciclo</dt>
                <dd>{selected.billing === 'annual' ? 'Anual' : 'Mensal'}</dd>
              </div>
              <div>
                <dt>Pagamento</dt>
                <dd>
                  {selected.paymentMethod === 'pix'
                    ? 'PIX'
                    : selected.paymentMethod === 'card'
                      ? 'Cartão'
                      : '—'}
                </dd>
              </div>
              <div>
                <dt>Fim do período</dt>
                <dd>{formatDate(selected.currentPeriodEnd)}</dd>
              </div>
              <div>
                <dt>Criado</dt>
                <dd>{formatDate(selected.createdAt)}</dd>
              </div>
              {selected.stripeSubscriptionId && (
                <div>
                  <dt>Stripe sub</dt>
                  <dd className="admin-mono">{selected.stripeSubscriptionId}</dd>
                </div>
              )}
              {selected.mercadoPagoPaymentId && (
                <div>
                  <dt>Mercado Pago</dt>
                  <dd className="admin-mono">{selected.mercadoPagoPaymentId}</dd>
                </div>
              )}
            </dl>

            {canEdit && (
            <div className="vip-detail__edit">
              <h3>Editar</h3>
              <label>
                Nick Minecraft
                <input
                  value={editNick}
                  onChange={(e) => setEditNick(e.target.value)}
                  maxLength={16}
                />
              </label>
              <label>
                Plano
                <select
                  value={editPlan}
                  onChange={(e) => setEditPlan(e.target.value)}
                >
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {shortPlan(p.name)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Ciclo
                <select
                  value={editBilling}
                  onChange={(e) => setEditBilling(e.target.value)}
                >
                  <option value="monthly">Mensal</option>
                  <option value="annual">Anual</option>
                </select>
              </label>
              <button
                type="button"
                className="admin-btn admin-btn--primary admin-btn--sm"
                disabled={actionBusy === 'update'}
                onClick={() =>
                  onUpdate(selected.id, {
                    minecraftNick: editNick,
                    planId: editPlan,
                    billing: editBilling,
                  })
                }
              >
                {actionBusy === 'update' ? 'Salvando…' : 'Salvar alterações'}
              </button>
            </div>
            )}

            {(canEdit || canDelete) && (
            <div className="vip-detail__actions">
              {canEdit && selected.status === 'active' && (
                <button
                  type="button"
                  className="admin-btn admin-btn--ghost"
                  disabled={Boolean(actionBusy)}
                  onClick={() => onReapply(selected.id)}
                >
                  <FaCheckCircle aria-hidden="true" />
                  {actionBusy === 'reapply' ? 'Sincronizando…' : 'Reaplicar cargos'}
                </button>
              )}
              {canEdit && selected.status === 'canceled' && (
                <button
                  type="button"
                  className="admin-btn admin-btn--primary"
                  disabled={Boolean(actionBusy)}
                  onClick={() =>
                    setConfirmDialog({
                      title: 'Reativar VIP',
                      message: `Reativar VIP de ${selected.minecraftNick}? Um novo ciclo será iniciado e os cargos Discord + LuckPerms serão sincronizados.`,
                      confirmLabel: 'Reativar',
                      tone: 'primary',
                      onConfirm: () =>
                        onReactivate?.(selected.id, {
                          minecraftNick: editNick || selected.minecraftNick,
                          planId: editPlan || selected.planId,
                          billing: editBilling || selected.billing,
                        }),
                    })
                  }
                >
                  <FaRedo aria-hidden="true" />
                  {actionBusy === 'reactivate' ? 'Reativando…' : 'Reativar VIP'}
                </button>
              )}
              {canEdit && selected.status === 'active' && (
                <button
                  type="button"
                  className="admin-btn admin-btn--ghost"
                  disabled={Boolean(actionBusy)}
                  onClick={() =>
                    onUpdate(selected.id, { extendPeriod: true })
                  }
                >
                  Estender +1 ciclo
                </button>
              )}
              {canEdit && selected.status !== 'canceled' && (
                <button
                  type="button"
                  className="admin-btn admin-btn--danger"
                  disabled={Boolean(actionBusy)}
                  onClick={() =>
                    setConfirmDialog({
                      title: 'Revogar VIP',
                      message: `Revogar VIP de ${selected.minecraftNick}? Os cargos no Discord e no LuckPerms serão removidos.`,
                      confirmLabel: 'Revogar',
                      tone: 'danger',
                      onConfirm: () => onRevoke(selected.id),
                    })
                  }
                >
                  <FaExclamationTriangle aria-hidden="true" />
                  {actionBusy === 'revoke' ? 'Revogando…' : 'Revogar VIP'}
                </button>
              )}
              {canDelete && (
              <button
                type="button"
                className="admin-btn admin-btn--danger"
                disabled={Boolean(actionBusy)}
                onClick={() =>
                  setConfirmDialog({
                    title: 'Excluir assinatura',
                    message: `Excluir permanentemente a assinatura de ${selected.minecraftNick}? Esta ação não pode ser desfeita.`,
                    confirmLabel: 'Excluir',
                    tone: 'danger',
                    onConfirm: () => onDelete?.(selected.id),
                  })
                }
              >
                <FaTrash aria-hidden="true" />
                {actionBusy === 'delete' ? 'Excluindo…' : 'Excluir assinatura'}
              </button>
              )}
            </div>
            )}

            {!canEdit && !canDelete && (
              <p className="admin-hint">Acesso somente leitura nesta área.</p>
            )}

          </aside>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        tone={confirmDialog?.tone || 'danger'}
        busy={Boolean(actionBusy)}
        onCancel={closeConfirm}
        onConfirm={runConfirm}
      />
    </section>
  );
};

export default VipPanel;
